import { Box, Typography } from "@mui/material";
import { Menu, MenuItem } from "@mui/material";
import Main from "components/Main/Main";
import QrScanner from "qr-scanner";
import React from "react";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useSearchParams } from "react-router-dom";
import useFlagBagController from "hooks/useFlagBagController";
import { ErrorCodes } from "services/api";
import useDataController from "hooks/useDataController";
import { FlagBagStatus, StatusAllowCollect, StatusCollected } from "services/api/flagbags/types";
import { StatusCode } from "services/api/flagbags/types";
import { StatusLabel } from "services/api/flagbags/types";
import { convertScanTypeToStatusCode, ScanType } from "types/scan";
import { useAuth } from "store/AuthProvider";
import { Setting } from "store/Setting";
import { useReservedForm } from "store/ReservedFormProvider";
import ReservedForm from "components/ReservedForm";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import OfflineTipDialog from "./dialog/offline-tip-dialog";
import { useNetworkStatus } from "hooks/useNetworkStatus";
import { translateErrorMessage } from "utils/statusTranslator";
import "./camera.css";
import { useData } from "store/DataProvider";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Button,
} from "@mui/material";
import { initIndexedDbIfMissingStore } from "utils/indexedDbRecovery";
import { convertStatusCodeToApiCode, isReservedFlagbag } from "utils/flagbagStatusUtils";

const Camera = () => {
	const auth = useAuth();
	const data = useData();
	const [searchParams, setSearchParams] = useSearchParams();
	const FlagBagController = useFlagBagController();
	const DataController = useDataController();
	const reservedForm = useReservedForm();
	const scanType: number = +searchParams.get("scanType") || ScanType.DISTRIBUTE_MODE;
	const offlineDB = initIndexedDbIfMissingStore("offline");
	const flagBagDB = initIndexedDbIfMissingStore("flagbag");
	const { isOnline } = useNetworkStatus();
	
	// 确认对话框状态
	const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
	const [pendingFlagBagStatus, setPendingFlagBagStatus] = React.useState<FlagBagStatus & { stationId?: string; stationName?: string } | null>(null);
	const [pendingFlagBagId, setPendingFlagBagId] = React.useState<string | null>(null);
	
	// Update scanTypeRef when scanType changes
	React.useEffect(() => {
		scanTypeRef.current = scanType;
	}, [scanType, searchParams]);
	const [result, setResult] = React.useState<any>("");
	const [offlineTipDialogOpen, setOfflineTipDialogOpen] = React.useState<boolean>(false);
	const [isOnlineProcessing, setIsOnlineProcessing] = React.useState<boolean>(false);
	const [qrCodeScanning, setQrCodeScanning] = React.useState(false);
	const qrCodeRef = React.useRef<HTMLVideoElement>();
	const qrCodeContainRef = React.useRef<HTMLElement>();
	const scannerRef = React.useRef<QrScanner | null>(null);
	const lastScanCodeRef = React.useRef<string | null>(null);
	const lastScanTimeRef = React.useRef<number>(0);
	const scanTypeRef = React.useRef<number>(scanType);

	React.useEffect(() => {
		if (!qrCodeRef.current) return;
		if (!isOnline) {
			setOfflineTipDialogOpen(true);
		}
		const scanner = new QrScanner(
			qrCodeRef.current,
			(result) => {
				if (result.data && lastScanCodeRef.current === result.data) {
					// do not scan the same code too within 15s
					if (Date.now() - lastScanTimeRef.current < 15000) {
						return;
					}
				}
				lastScanCodeRef.current = result.data;
				lastScanTimeRef.current = Date.now();
				handleScan(result);
			},
			{
				onDecodeError: (error) => {
					//enqueueSnackbar(error.toString(), { variant: "error" });
				},
				highlightScanRegion: true,
				highlightCodeOutline: true,
				maxScansPerSecond: 1,
				calculateScanRegion: (v) => {
					const smallestDimension = Math.min(v.clientWidth, v.clientHeight);

					// Make scan region smaller to match better small qr codes
					const scanRegionSize = Math.round((3 / 4) * smallestDimension);

					let region: QrScanner.ScanRegion = {
						x: Math.round((v.videoWidth - scanRegionSize) / 2),
						y: Math.round((v.videoHeight - scanRegionSize) / 4),
						width: scanRegionSize,
						height: scanRegionSize,
					};
					return region;
				},
			}
		);

		scannerRef.current = scanner;

		scanner
			.start()
			.catch((error) => {
				if (!error) return;
				if (error.toString().startsWith("AbortError")) {
					window.location.reload();
				} else {
					addSnackbar(error.toString(), "error", false, false);
				}
			});

		return () => {
			if (scannerRef.current) {
				scannerRef.current.destroy();
				scannerRef.current = null;
			}
		};
	}, []);

	React.useEffect(() => {
		lastScanCodeRef.current = null;
	}, [scanType]);

	const getTitleByScanType = (type: number) => {
		const label = {
			[ScanType.DISTRIBUTE_MODE]: "出旗袋",
			[ScanType.COLLECT_MODE]: "實收袋",
			[ScanType.COLLECT_EMPTY_MODE]: "收吉袋",
			[ScanType.COLLECT_BANK_MODE]: "實收袋&入銀行",
		};
		return label[type] || "未知模式";
	};

	const getScanStatusLabelText = (status?: StatusCode) => {
		if (!status) return "未知狀態";
		const scanOperationLabel: Partial<Record<StatusCode, string>> = {
			[StatusCode.DISTRIBUTED]: getTitleByScanType(ScanType.DISTRIBUTE_MODE),
			[StatusCode.COLLECTED]: getTitleByScanType(ScanType.COLLECT_MODE),
			[StatusCode.COLLECTED_EMPTY]: getTitleByScanType(ScanType.COLLECT_EMPTY_MODE),
			[StatusCode.BANK]: getTitleByScanType(ScanType.COLLECT_BANK_MODE),
		};
		return scanOperationLabel[status] || StatusLabel[status] || "未知狀態";
	};

	const getScanUpdateSuccessContent = (
		currentStatus: StatusCode | undefined,
		flagbagNum: string,
		offlineMode = false,
		operationScanType: number = scanTypeRef.current,
		currentStatusLabel?: string
	) => {
		const offlineText = offlineMode ? "（離線紀錄）" : "";
		return (
			<Box sx={{ whiteSpace: "pre-line" }}>
				{`此旗袋為「${currentStatusLabel || getScanStatusLabelText(currentStatus)}」\n已更新為「${getTitleByScanType(operationScanType)}」${offlineText}\n旗袋編號：${flagbagNum}`}
			</Box>
		);
	};

	// 处理确认对话框的确认操作
	const handleConfirmOtherStation = () => {
		setConfirmDialogOpen(false);
		if (pendingFlagBagStatus && pendingFlagBagId) {
			// 继续执行扫描回收逻辑
			if (isOnline) {
				singleScanOnline(pendingFlagBagStatus);
			} else {
				singleScanOffline(pendingFlagBagStatus);
			}
		}
		setPendingFlagBagStatus(null);
		setPendingFlagBagId(null);
	};

	// 处理确认对话框的取消操作
	const handleCancelOtherStation = () => {
		setConfirmDialogOpen(false);
		setPendingFlagBagStatus(null);
		setPendingFlagBagId(null);
		startScan();
	};
	
	const title = React.useMemo(() => getTitleByScanType(scanType), [scanType]);

	const startScan = (flagBagId?: string) => {
		if (scannerRef.current) {
			(scannerRef.current as any)._active = true;
			(scannerRef.current as any)._scanFrame();
		}
	};
	const noSuchBag = (flagbagNum?: string) => {
		console.log("noSuchBag", flagbagNum);
		const message = flagbagNum 
			? `旗袋不屬於您管理的旗站\n旗袋編號：${flagbagNum}`
			: "旗袋不屬於您管理的旗站";
		addSnackbar(message, "error", true, false);
		startScan();
	};

	const handleCrossStationFlagBag = (flagBagStatus: FlagBagStatus & { stationId?: string; stationName?: string }, flagBagId: string) => {
		const currentStationId = data.getStationId();
		if (!flagBagStatus.stationId || !currentStationId || flagBagStatus.stationId === currentStationId) {
			return false;
		}

		// 出旗袋模式不允許跨站旗袋，直接拒絕
		if (scanTypeRef.current === ScanType.DISTRIBUTE_MODE) {

			addSnackbar(`此旗袋為其他旗站之旗袋\n旗袋編號：${flagBagStatus.flagbagNum}`, "error", false, false);
			startScan(flagBagId);
			return true;
		}

		setPendingFlagBagStatus(flagBagStatus);
		setPendingFlagBagId(flagBagId);
		setConfirmDialogOpen(true);
		return true;
	};

	const finishReservedBag = async ({ volunteerId }) => {
		// 離線模式處理
		if (!isOnline) {
			try {
				const flagbag = await FlagBagController.getBag(result);
				if (!flagbag) {
					addSnackbar("找不到此旗袋", "error", false, false);
					startScan();
					return;
				}

				// 使用專門的離線預留旗袋函數，傳入 volunteerId
				await FlagBagController.addOfflineDataSpare(result, volunteerId);
				
				// 更新本地狀態為未收（與在線模式的 checkOutSpareFlagBag 邏輯一致）
				await FlagBagController.setBagStatus(result, StatusCode.DISTRIBUTED);
				
				addSnackbar(
					getScanUpdateSuccessContent(
						flagbag.currentStatus,
						flagbag.flagbagNum,
						true,
						ScanType.DISTRIBUTE_MODE,
						"後備"
					),
					"warning",
					false,
					false
				);
				
				setResult("");
				startScan(result);
			} catch (error: any) {
				console.error("Offline reserved bag checkout failed:", error);
				const rawErrorMsg = error?.errorMsg || error;
				addSnackbar(translateErrorMessage(rawErrorMsg) || "離線紀錄失敗", "error", false, false);
				startScan();
			}
			return;
		}

		// 在線模式處理
		setIsOnlineProcessing(true);

		try {
			const resp: any = await FlagBagController.checkOutSpareFlagBag(result, volunteerId);
			
			// 處理不同的響應格式
			let flagbagNum = result;
			if (resp?.updatedFlagbags?.update) {
				const updatedBag = resp.updatedFlagbags.update.find((updateFlagBag: any) => updateFlagBag.id === result);
				flagbagNum = updatedBag?.flagbagNum || result;
			} else if (resp?.data?.flagbag) {
				flagbagNum = resp.data.flagbag.flagbagNum || result;
			} else if (resp?.flagbag) {
				flagbagNum = resp.flagbag.flagbagNum || result;
			}

			// 立即更新本地数据库状态，包括完整的状态信息
			await FlagBagController.setBagStatus(result, StatusCode.DISTRIBUTED);
			const updatedBag = await FlagBagController.getBag(result);
			if (updatedBag) {
				const allowedStatusList = DataController.getComingStatusListLocal(StatusCode.DISTRIBUTED);
				await flagBagDB.update({
					...updatedBag,
					status: StatusCode.DISTRIBUTED,
					currentStatus: StatusCode.DISTRIBUTED,
					allowedStatusList,
					volunteerId,
				});
			}

			addSnackbar(
				getScanUpdateSuccessContent(undefined, flagbagNum, false, ScanType.DISTRIBUTE_MODE, "後備"),
				"success",
				false,
				false
			);

			setIsOnlineProcessing(false);
			setResult("");
			startScan(result);
		} catch (error: any) {
			setIsOnlineProcessing(false);
			const rawErrorMsg = error?.errorMsg || error;
			addSnackbar(translateErrorMessage(rawErrorMsg), "error", false, false);
			startScan();
		}
	};

	const handleReservedBag = async (flagBagStatus: FlagBagStatus) => {
		// Allow scanning flagbags from any station - no station check needed
		reservedForm.open(flagBagStatus.flagBagId);
	};

	const invalidBagUpdate = (status: StatusCode, flagbagNum: string) => {
		const statusText = StatusLabel[status] || '未知狀態';
		addSnackbar(`此旗袋為「${statusText}」旗袋\n旗袋編號：${flagbagNum}`, "error", false, false);
		startScan();
	};
	const isValidBagUpdateOnline = (status: FlagBagStatus, newType: ScanType) => {
		console.log("isValidBagUpdateOnline", status, newType);
		switch (newType) {
			case ScanType.DISTRIBUTE_MODE:
				if (status.allowedStatusList.includes(StatusCode.DISTRIBUTED)) return true;
				break;
			case ScanType.COLLECT_MODE:
				if (status.allowedStatusList.includes(StatusCode.COLLECTED)) return true;
				break;
			case ScanType.COLLECT_EMPTY_MODE:
				if (status.allowedStatusList.includes(StatusCode.COLLECTED_EMPTY)) return true;
				break;
			case ScanType.COLLECT_BANK_MODE:
				if (status.allowedStatusList.includes(StatusCode.BANK)) return true;
				break;
			default:
				return false;
		}
		return false;
	};
	const updateBagOffline = React.useCallback(async (flagBagId: string, statusCode: StatusCode, flagbag: FlagBagStatus) => {
		try {
			// 確保 status 字段存在，如果不存在則使用 currentStatus
			const currentStatus = flagbag.currentStatus;
						
			// 如果狀態仍然是 undefined，顯示錯誤
			if (currentStatus === undefined || currentStatus === null) {
				enqueueSnackbar(`旗袋狀態錯誤\n旗袋編號：${flagbag.flagbagNum || flagBagId}`, { variant: "error" });
				return;
			}
			let pendingStatus = undefined;

			try {
				const pendingActions = await offlineDB.getAll();
				if (pendingActions && Array.isArray(pendingActions)) {
					const targetPending = pendingActions.find((action: any) => action.flagBagID === flagBagId && action.status !== undefined);
					if (targetPending) {
						pendingStatus = targetPending.status;
					}
				}
			} catch (error) {
				console.warn("讀取 Pending Action 錯誤", error);
			}
			
			if (Object.values(StatusCollected).includes(statusCode as unknown as StatusCollected)
				&&
				!Object.values(StatusCollected).includes(currentStatus as unknown as StatusCollected)
			) {
				if (
					(pendingStatus && !Object.values(StatusAllowCollect).includes(pendingStatus as unknown as StatusAllowCollect)) ||
					!Object.values(StatusAllowCollect).includes(currentStatus as unknown as StatusAllowCollect)
				) {
					enqueueSnackbar(`請先將旗袋 ${flagbag.flagbagNum || flagBagId} 設定為未收`, { variant: "warning" });
					return;
				}

			}
			const flagBagStatus: FlagBagStatus = {
				flagBagId: flagbag.flagBagId,
				currentStatus,
				flagbagNum: flagbag.flagbagNum || flagBagId,
				volunteerName: flagbag.volunteerName || '',
				allowedStatusList: DataController.getComingStatusListLocal(currentStatus),
				bagType: flagbag.bagType,
			};

			// 將操作添加到離線數據庫
			await FlagBagController.addOfflineDataIndividual(flagBagId, statusCode, false);
			await FlagBagController.setBagStatus(flagBagId, statusCode);
			
			// 顯示離線記錄提示
			addSnackbar(
				getScanUpdateSuccessContent(currentStatus, flagBagStatus.flagbagNum, true),
				"warning",
				false,
				false
			);
			
			startScan(flagBagId);
		} catch (error) {
			console.error("Offline update failed:", error);
			addSnackbar(`離線紀錄失敗\n旗袋編號：${flagbag.flagbagNum || flagBagId}`, "error", false, false);
			startScan(flagBagId);
		}
	}, [FlagBagController, DataController, offlineDB]);

	const updateBag = React.useCallback((flagBagId: string, newServerStatus: StatusCode, flagBagStatus: FlagBagStatus, offlineMode: boolean = false) => {
		if (Setting.USE_ADVANCE_SCAN) {
			// 将当前状态转换为 API code
			const currentApiStatus = convertStatusCodeToApiCode(flagBagStatus.currentStatus);
			FlagBagController.updateFlagBagStatusToServerAdvance(flagBagId, newServerStatus, currentApiStatus, false)
				.then((resp) => {
					addSnackbar(
						getScanUpdateSuccessContent(flagBagStatus.currentStatus, flagBagStatus.flagbagNum, offlineMode),
						offlineMode ? "warning" : "success",
						false,
						false
					);

					setIsOnlineProcessing(false);
					startScan(flagBagId);
				})
				.catch(async (error) => {
					setIsOnlineProcessing(false);

					// 如果服务器返回状态错误（通常是状态不同步），重新从服务器获取最新状态
					if (error.errorCode === ErrorCodes.ERR_FLAG_BAG_WRONG_STATUS || 
						(error.errorMsg && (error.errorMsg.includes("已分发") || error.errorMsg.includes("狀態錯誤")))) {
						try {
							// 从服务器重新获取最新状态
							const latestStatus = await FlagBagController.FlagBagStatus(flagBagId);
							
							// 更新本地数据库
							await FlagBagController.setBagStatus(flagBagId, latestStatus.currentStatus);
							const updatedBag = await FlagBagController.getBag(flagBagId);
							if (updatedBag) {
								const allowedStatusList = DataController.getComingStatusListLocal(latestStatus.currentStatus);
								await flagBagDB.update({
									...updatedBag,
									status: latestStatus.currentStatus,
									currentStatus: latestStatus.currentStatus,
									allowedStatusList,
								});
							}
							
							addSnackbar(`旗袋狀態已更新\n旗袋編號：${flagBagStatus.flagbagNum}\n現時狀態：${StatusLabel[latestStatus.currentStatus]}`, "info", false, false);
						} catch (syncError) {
							console.error("Failed to sync status:", syncError);
							addSnackbar(`旗袋狀態錯誤\n旗袋編號：${flagBagStatus.flagbagNum}`, "error", false, false);
						}
					} else if (error.errorCode === ErrorCodes.ERR_FLAG_BAG_NOT_IN_SAME_STATION) {
						addSnackbar(`此旗袋為其他旗站之旗袋\n旗袋編號：${flagBagStatus.flagbagNum}`, "error", false, false);
					} else if (error.errorCode) {
						// 有明确的错误码才显示错误
						addSnackbar(`更新失敗\n旗袋編號：${flagBagStatus.flagbagNum}`, "error", false, false);
					}
					
					startScan(flagBagId);
				});
		} else {
			//ToDo: updateFlagBagStatusToServerAdvance
		}
	}, [title, FlagBagController, DataController, flagBagDB]);

	const singleScanOffline = React.useCallback((flagBagStatus: FlagBagStatus) => {
		const status = flagBagStatus.currentStatus;
		const flagBagId = flagBagStatus.flagBagId;
		const currentScanType = scanTypeRef.current; // 使用 ref 获取最新值

		// 处理未分配状态：出旗袋扫描时应该提示错误
		if (status === StatusCode.NOT_ASSIGN) {
			if (currentScanType === ScanType.DISTRIBUTE_MODE) {
				addSnackbar(`此旗袋未分配，請先分配旗袋\n旗袋編號：${flagBagStatus.flagbagNum}`, "error", false, false);
				startScan();
				return;
			} else {
				invalidBagUpdate(status, flagBagStatus.flagbagNum);
				return;
			}
		}

		if (isReservedFlagbag(flagBagStatus)) {
			if (currentScanType === ScanType.DISTRIBUTE_MODE) {
				// 離線模式也支持預留旗袋出袋，打開表單讓用戶填寫信息
				handleReservedBag(flagBagStatus);
			} else {
				invalidBagUpdate(status, flagBagStatus.flagbagNum);
			}          
		} else {
			if (isValidBagUpdateOnline(flagBagStatus, currentScanType)) {
				const statusCode = convertScanTypeToStatusCode(currentScanType);
				updateBagOffline(flagBagId, statusCode, flagBagStatus);
			} else {
				invalidBagUpdate(status, flagBagStatus.flagbagNum);
			}
		}
	}, [FlagBagController, DataController, offlineDB]); // 移除 title 和 scanType 依赖

	const singleScanOnline = React.useCallback((flagBagStatus: FlagBagStatus, offlineMode: boolean = false) => {
		const currentStatus = flagBagStatus.currentStatus;
		const flagBagId = flagBagStatus.flagBagId;
		const currentScanType = scanTypeRef.current; // 使用 ref 获取最新值
		
		// 处理未分配状态：出旗袋扫描时应该提示错误
		if (currentStatus === StatusCode.NOT_ASSIGN) {
			if (currentScanType === ScanType.DISTRIBUTE_MODE) {
				addSnackbar(`此旗袋未分配，請先分配旗袋\n旗袋編號：${flagBagStatus.flagbagNum}`, "error", false, false);
				startScan();
				return;
			} else {
				invalidBagUpdate(currentStatus, flagBagStatus.flagbagNum);
				return;
			}
		}
		
		if (isReservedFlagbag(flagBagStatus)) {
			if (currentScanType === ScanType.DISTRIBUTE_MODE) {
				handleReservedBag(flagBagStatus);
			} else {
				invalidBagUpdate(currentStatus, flagBagStatus.flagbagNum);
			}          
		} else {
			const isValid = isValidBagUpdateOnline(flagBagStatus, currentScanType);
			
			if (isValid) {
				setIsOnlineProcessing(true);
				const newServerStatus = convertScanTypeToStatusCode(currentScanType);
				if (currentScanType === ScanType.COLLECT_BANK_MODE) {
					FlagBagController.checkExcessBankQuotaAndShowPopUp(1)
						.then((resp) => {
							const bankAction = () => {
								return (
									<>
										<button
											onClick={() => {
												setIsOnlineProcessing(false);
												startScan(flagBagId);
												closeSnackbar(snackbarId);
											}}>
											取消
										</button>
										<button
											onClick={() => {
												updateBag(flagBagId, newServerStatus, flagBagStatus, offlineMode);
											}}>
											確定
										</button>
									</>
								);
							};
							const snackbarId = enqueueSnackbar('旗袋數量已達入銀行上限，\n是否繼續 "實收袋+入銀行" ?', {
								variant: "warning",
								action: bankAction,
								persist: true,
							});
						})
						.catch((error) => {
							updateBag(flagBagId, newServerStatus, flagBagStatus, offlineMode);
						});
				} else {
					updateBag(flagBagId, newServerStatus, flagBagStatus, offlineMode);
				}
			} else {
				invalidBagUpdate(currentStatus, flagBagStatus.flagbagNum);
			}
		}
	}, [FlagBagController, DataController, flagBagDB]); // 移除 title 和 scanType 依赖，使用 ref

	const handleScan = React.useCallback(async (result: QrScanner.ScanResult) => {
		if (qrCodeScanning || isOnlineProcessing) return;

		// Support both formats: "flag-bag://id" and just "id"
		let flagBagId: string;
		if (result.data.includes("flag-bag://")) {
			flagBagId = result.data.replace("flag-bag://", "");
		} else if (result.data.length === 24) {
			// MongoDB ObjectId is 24 characters hex string
			flagBagId = result.data;
		} else {
			noSuchBag();
			return;
		}

		setResult(flagBagId);

		// 如果離線，直接從本地數據庫獲取數據
		if (!isOnline) {
			const flagbag = await FlagBagController.getBag(flagBagId);
			
			if (!flagbag) {
				noSuchBag();
				return;
			}

			// 確保 status 字段存在，如果不存在則使用 currentStatus
			const currentStatus = flagbag.status ?? flagbag.currentStatus;
			
			// 如果狀態仍然是 undefined，顯示錯誤
			if (currentStatus === undefined || currentStatus === null) {
				addSnackbar(`旗袋狀態錯誤\n旗袋編號：${flagbag.flagbagNum || flagBagId}`, "error", false, false);
				startScan(flagBagId);
				return;
			}

			const flagBagStatus: FlagBagStatus & { stationId?: string; stationName?: string } = {
				flagBagId: flagbag.id,
				currentStatus,
				flagbagNum: flagbag.flagbagNum || flagBagId,
				volunteerName: flagbag.volunteerName || '',
				allowedStatusList: DataController.getComingStatusListLocal(currentStatus),
				bagType: flagbag.bagType,
				stationId: flagbag.stationId,
				stationName: flagbag.stationName,
			};
			
			if (handleCrossStationFlagBag(flagBagStatus, flagBagId)) return;
			
			// 離線模式處理
			singleScanOffline(flagBagStatus);
			return;
		}

		// 在線模式：始终从服务器获取最新状态以确保数据同步
		await FlagBagController.FlagBagStatus(flagBagId)
			.then(async (resp) => {
				// 更新本地數據庫中的旗袋狀態和完整信息
				try {
					const localBag = await FlagBagController.getBag(flagBagId);
					if (localBag) {
						await FlagBagController.setBagStatus(flagBagId, resp.currentStatus);
						// 同时更新完整的状态信息
						const allowedStatusList = DataController.getComingStatusListLocal(resp.currentStatus);
						await flagBagDB.update({
							...localBag,
							status: resp.currentStatus,
							currentStatus: resp.currentStatus,
							allowedStatusList,
						});
					}
				} catch (err) {
					console.warn("Failed to update local bag status:", err);
				}
				
				const respWithStation = resp as FlagBagStatus & { stationId?: string; stationName?: string };
				if (handleCrossStationFlagBag({ ...respWithStation, flagBagId }, flagBagId)) return;
				
				singleScanOnline({ ...resp, flagBagId });
			})
			.catch((error) => {
				FlagBagController.getBag(flagBagId).then((flagbag) => {
					switch (error.errorCode) {
						case ErrorCodes.NETWORK_ERROR:
							if (!flagbag) {
								noSuchBag();
								return;
							}

							const currentStatus = flagbag.status;

							const flagBagStatus: FlagBagStatus & { stationId?: string; stationName?: string } = {
								flagBagId: flagbag.id,
								currentStatus,
								flagbagNum: flagbag.flagbagNum,
								volunteerName: flagbag.volunteerName,
								allowedStatusList: DataController.getComingStatusListLocal(currentStatus),
								bagType: flagbag.bagType,
								stationId: flagbag.stationId,
								stationName: flagbag.stationName,
							};
							
							if (handleCrossStationFlagBag(flagBagStatus, flagBagId)) return;
							
							singleScanOffline(flagBagStatus);

						break;
					case ErrorCodes.ERR_FLAG_BAG_NOT_IN_EVENT:
						addSnackbar(`此旗袋為其他活動之旗袋\n旗袋編號：${flagbag.flagbagNum}`, "error", false, false); // 不持久化，自动关闭
						break;
					case ErrorCodes.ERR_FLAG_BAG_WRONG_STATUS:
						addSnackbar(`旗袋狀態錯誤\n旗袋編號：${flagbag.flagbagNum}`, "error", false, false); // 不持久化，自动关闭
						break;
					default:
						noSuchBag();
				}
					startScan(flagBagId);
				});
				return true;
			});
	}, [isOnline, qrCodeScanning, isOnlineProcessing, FlagBagController, DataController, singleScanOnline, singleScanOffline, flagBagDB, data]);

	React.useEffect(() => {
		const disabledScrollHandler = (e: WheelEvent) => {
			e.preventDefault();
		};
		window.addEventListener("scroll", disabledScrollHandler);
		return () => window.removeEventListener("scroll", disabledScrollHandler);
	}, []);

	React.useEffect(() => {
		return () => {
			const snackbar = document.getElementsByClassName("snackbarContainer");
			if (snackbar.length > 0) {
				snackbar[0].classList.add("snackbarContainer");
			}
			closeSnackbar();
		};
	}, []);

	const addSnackbar = (value: React.ReactNode, variant: any, preventDuplicate: boolean = false, persist: boolean = false) => {
		console.trace("[Camera:addSnackbar]", { value, variant, preventDuplicate, persist });
		console.log("[Camera:addSnackbar]", { value, variant, preventDuplicate, persist });
		return enqueueSnackbar(value, {
			variant,
			preventDuplicate,
			persist,
			autoHideDuration: persist ? undefined : 3000,
		});
	};

	return (
		<Main
			disableNavigationBar
			enableBackToPrevPage
			colorInvert
			titleAlign="left"
			title={
				<ScanModeMenu title={title} searchParams={searchParams} setSearchParams={setSearchParams} />
			}
		>
		<Box
			ref={qrCodeContainRef}
			display="flex"
			width={1}
			flexGrow={1}
			flexDirection={"column"}
			overflow={"hidden"}
			minHeight={"100vh"}
			className={isOnline ? "scanner-online" : "scanner-offline"}
			sx={{ backgroundColor: "rgb(4, 3, 37)" }}>
			<Box
				width={1}
				display="flex"
				flexGrow={1}
				component="video"
				sx={{ objectFit: "cover" }}
				ref={qrCodeRef}
			/>
		</Box>
			{/* <Backdrop
				sx={(theme) => ({ zIndex: theme.zIndex.drawer + 1 })}
				open={isOnlineProcessing}>
				<CircularProgress color="primary" />
			</Backdrop> */}
			{reservedForm.reservedFormOpen && (
				<ReservedForm handleSubmit={finishReservedBag} />
			)}
			<OfflineTipDialog open={offlineTipDialogOpen} />
			
			{/* 确认回收其他站点旗袋的对话框 */}
			<Dialog
				open={confirmDialogOpen}
				onClose={handleCancelOtherStation}
				aria-labelledby="confirm-dialog-title"
				aria-describedby="confirm-dialog-description"
			>
				<DialogTitle id="confirm-dialog-title">
					確認掃描其他旗站旗袋
				</DialogTitle>
				<DialogContent>
					<DialogContentText id="confirm-dialog-description">
						{pendingFlagBagStatus && (
							<>
								是否繼續掃描其他旗站的旗袋？
								{pendingFlagBagStatus.stationName && (
									<>
										<br />
										<br />
										旗袋所屬旗站：{pendingFlagBagStatus.stationName}
									</>
								)}
								<br />
								<br />
								旗袋編號：{pendingFlagBagStatus.flagbagNum}
							</>
						)}
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelOtherStation} color="inherit">
						取消
					</Button>
					<Button onClick={handleConfirmOtherStation} variant="contained" color="primary" autoFocus>
						確定
					</Button>
				</DialogActions>
			</Dialog>
		</Main>
	);
};

const ScanModeMenu = ({ 
	title, 
	searchParams, 
	setSearchParams 
}: { 
	title: string; 
	searchParams: URLSearchParams;
	setSearchParams: (params: URLSearchParams) => void;
}) => {
	const [mode, setMode] = React.useState<null | HTMLElement>(null);
	const open = Boolean(mode);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setMode(event.currentTarget);
	}

	const handleClose = () => {
		setMode(null);
	}

	const handleScanModeChange = (scanType: ScanType) => {
		const newParams = new URLSearchParams(searchParams);
		newParams.set("scanType", scanType.toString());
		setSearchParams(newParams);
		handleClose();
	}

	return (
		<>
		<Box
			display="flex"
			alignItems="center"
			gap={1}
			component="button"
			onClick={handleClick}
			sx={{
				background: "none",
				border: "none",
				padding: 0,
				cursor: "pointer",
				color: "inherit",
				font: "inherit",
			}}
		>
			<Typography variant="h6">{title}</Typography>
			<ArrowDropDownIcon />
		</Box>
			<Menu
				anchorEl={mode}
				open={open}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
			>
				<MenuItem onClick={() => handleScanModeChange(ScanType.DISTRIBUTE_MODE)}>
					出旗袋
				</MenuItem>
				<MenuItem onClick={() => handleScanModeChange(ScanType.COLLECT_MODE)}>
					實收袋
				</MenuItem>
				<MenuItem onClick={() => handleScanModeChange(ScanType.COLLECT_EMPTY_MODE)}>
					收吉袋
				</MenuItem>
				<MenuItem onClick={() => handleScanModeChange(ScanType.COLLECT_BANK_MODE)}>
					實收袋&入銀行
				</MenuItem>
			</Menu>
		</>
	)
}

export default Camera;
